import PropTypes from 'prop-types';
import { Button, Icon, Popover2 } from '@evertrue/et-components';
import { getRoundedPercentage } from '../utils/cadence-data-mappers';

/** Colored status pill for Active/Draft/Paused/Completed. */
export const StatusBadge = ({ status }) => (
  <span className={`cadences-v2-table--status cadences-v2-table--status-${status?.toLowerCase()}`}>{status}</span>
);
StatusBadge.propTypes = { status: PropTypes.string };

/** Avatar initials + name cell, shows "You" for the current user. */
export const CreatedByCell = ({ createdBy, currentUserId }) => {
  const isYou = createdBy?.id && String(createdBy.id) === String(currentUserId);
  const hasName = createdBy?.name && createdBy.name !== 'Removed User';
  const displayName = isYou ? 'You' : hasName ? createdBy.name : createdBy?.id ? 'Unknown User' : '\u2014';
  return (
    <div className="cadences-v2-table--created-by">
      {hasName && createdBy?.initials ? (
        <span className="cadences-v2-table--avatar">{createdBy.initials}</span>
      ) : (
        <Icon icon="person" />
      )}
      <span>{displayName}</span>
    </div>
  );
};
CreatedByCell.propTypes = {
  createdBy: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    initials: PropTypes.string,
  }),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/** Compact progress bar with goal label, percentage, and target. */
export const GoalPerformanceCell = ({ goalName, metric, target }) => {
  if (!goalName) return '\u2014';

  const hasMetric = metric !== undefined && metric !== null;
  const hasTarget = target !== undefined && target !== null && target > 0;
  const pctOfTarget = hasMetric && hasTarget ? getRoundedPercentage(metric, target) : 0;
  const barWidth = hasMetric && hasTarget ? Math.min((metric / target) * 100, 100) : 0;

  return (
    <div className="cadences-v2-goal-cell">
      <div className="cadences-v2-goal-cell--header">
        <span className="cadences-v2-goal-cell--name">{goalName || 'Goal'}</span>
      </div>
      <div className="cadences-v2-goal-cell--bar-row">
        <div className="cadences-v2-goal-cell--bar-track">
          <div className="cadences-v2-goal-cell--bar-fill" style={{ width: `${barWidth}%` }} />
        </div>
        {hasMetric && <span className="cadences-v2-goal-cell--pct">{metric}%</span>}
      </div>
      <div className="cadences-v2-goal-cell--footer">
        <span className="cadences-v2-goal-cell--progress">
          {hasMetric ? `${pctOfTarget}% of target` : 'No data yet'}
        </span>
        {hasTarget && <span className="cadences-v2-goal-cell--target">Target: {target}%</span>}
      </div>
    </div>
  );
};
GoalPerformanceCell.propTypes = {
  goalName: PropTypes.string,
  metric: PropTypes.number,
  target: PropTypes.number,
};

/** Icon + text badge for Email/SMS channel. */
export const ChannelBadge = ({ channel }) => (
  <div className="cadences-v2-table--channel">
    <Icon icon={channel === 'Email' ? 'email' : 'filter-list'} />
    <span>{channel}</span>
  </div>
);
ChannelBadge.propTypes = { channel: PropTypes.string };

/** Colored type badge for Cadence/Prospect Plan/Bulk Message. */
export const TypeBadge = ({ type }) => (
  <span
    className={`cadences-v2-table--type-badge cadences-v2-table--type-badge-${type?.toLowerCase().replace(/\s+/g, '-')}`}
  >
    {type}
  </span>
);
TypeBadge.propTypes = { type: PropTypes.string };

/** Tag pills display for a row. */
export const TagsCell = ({ tags }) => {
  if (!tags || tags.length === 0) return '\u2014';
  return (
    <div className="cadences-v2-table--name-tags">
      {tags.map((tag) => (
        <span key={tag} className="cadences-v2-table--tag-badge">
          {tag}
        </span>
      ))}
    </div>
  );
};
TagsCell.propTypes = { tags: PropTypes.arrayOf(PropTypes.string) };

/**
 * Renders a three-dot action dropdown menu for cadence/bulk-message/template rows.
 * @param {Object} params
 * @param {string} params.type - 'cadence' | 'bulk-message' | 'template'
 * @param {Object} params.row - The row data object
 * @param {Object} params.actions - Callback functions from the controller
 */
/**
 * Derive available actions from frontend logic.
 * Checks cadence type, status, and permission flags (canEdit/canDelete/ownerUserId).
 * Used as the primary source of truth — backend availableActions overrides only when non-empty.
 *
 * @param {Object} raw - Raw cadence object from API (must include ownerUserId)
 * @param {string|number} [currentUserId] - Current user's ID (from EverTrue.store or UserStore)
 */
export const getAvailableActionsFromFrontend = (raw, currentUserId) => {
  if (!raw) return [];
  const canView = !!raw.canView;
  const canEdit = !!raw.canEdit;
  const canDelete = !!raw.canDelete;
  const isTemplate = !!raw.isTemplate;
  const status = (raw.displayStatusLabel || '').toLowerCase();
  // Share and recipient management are truly owner-only — canDelete can be granted via sharing,
  // so we check the cadence's ownerUserId directly.
  const isCadenceOwner = currentUserId != null && String(raw.ownerUserId) === String(currentUserId);
  const computed = [];
  // Viewer and above: non-destructive read actions
  if (canView) {
    computed.push('duplicate');
    if (!isTemplate) {
      computed.push('save_as_blueprint');
    }
  }
  // Editor and above: content editing
  if (canEdit) {
    computed.push('edit');
  }
  // canDelete: status transitions and archive (can be granted via sharing)
  if (canDelete) {
    if (!isTemplate) {
      if (status === 'active') computed.push('pause');
      else if (status === 'paused') computed.push('resume');
      if (status === 'draft') computed.push('activate');
    }
    computed.push('archive');
  }
  // Owner only: sharing
  if (isCadenceOwner) {
    computed.push('share');
  }
  return computed;
};

export const ActionDropdown = ({ type, row, actions, currentUserId }) => {
  const id = row.id;
  const name = row.title || row.name;
  const status = row.status;
  const isArchived = !!row._isArchived;

  // Frontend-computed actions based on cadence type, status, and permissions.
  const availableActions = getAvailableActionsFromFrontend(row._raw, currentUserId);

  return (
    <Popover2
      trigger={
        <Button type="simple" className="cadences-v2-table--action-btn">
          <Icon icon="more-horiz" />
        </Button>
      }
      contentClassName="cadences-v2-action-dropdown"
    >
      {({ closePopover }) => (
        <div className="cadences-v2-action-dropdown--menu">
          <button
            type="button"
            className="cadences-v2-action-dropdown--item"
            onClick={() => {
              closePopover();
              actions.onView(type, id);
            }}
          >
            <Icon icon="eye" />
            <span>View details</span>
          </button>
          {availableActions.includes('duplicate') && isArchived && actions.onDuplicate && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onDuplicate(type, id, { archived: true });
              }}
            >
              <Icon icon="content-copy" iconKit="gt2" />
              <span>Duplicate as Draft</span>
            </button>
          )}
          {isArchived && type !== 'template' && actions.onSaveAsShell && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onSaveAsShell(id, name);
              }}
            >
              <Icon icon="note-add" />
              <span>Save as Blueprint</span>
            </button>
          )}
          {availableActions.includes('edit') && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onEdit(type, id);
              }}
            >
              <Icon icon="edit" />
              <span>Edit</span>
            </button>
          )}
          {availableActions.includes('edit') && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onStartRename(id, name);
              }}
            >
              <Icon icon="edit" />
              <span>Rename</span>
            </button>
          )}
          {(availableActions.includes('pause') ||
            availableActions.includes('resume') ||
            availableActions.includes('activate')) && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onStatusToggle(id, status);
              }}
            >
              <Icon icon={availableActions.includes('pause') ? 'pause' : 'play-arrow'} />
              <span>
                {availableActions.includes('pause')
                  ? 'Pause'
                  : availableActions.includes('resume')
                    ? 'Resume'
                    : 'Activate'}
              </span>
            </button>
          )}
          {availableActions.includes('share') && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onShare(id, name);
              }}
            >
              <Icon icon="users" />
              <span>Share</span>
            </button>
          )}
          {availableActions.includes('duplicate') && !isArchived && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onDuplicate(type, id);
              }}
            >
              <Icon icon="content-copy" iconKit="gt2" />
              <span>Duplicate</span>
            </button>
          )}
          {availableActions.includes('save_as_blueprint') && !isArchived && type !== 'template' && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onSaveAsShell(id, name);
              }}
            >
              <Icon icon="note-add" />
              <span>Save as Cadence Blueprint</span>
            </button>
          )}
          {!isArchived && type === 'template' && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item"
              onClick={() => {
                closePopover();
                actions.onCreateFromShell(id, row._raw?.cadenceType);
              }}
            >
              <Icon icon="add" />
              <span>Create {row._raw?.cadenceType === 'bulk_message' ? 'Bulk Message' : 'Cadence'} from Blueprint</span>
            </button>
          )}
          {availableActions.includes('archive') && (
            <button
              type="button"
              className="cadences-v2-action-dropdown--item cadences-v2-action-dropdown--item-danger"
              onClick={() => {
                closePopover();
                actions.onDelete(type, id);
              }}
            >
              <Icon icon="archive" />
              <span>Archive</span>
            </button>
          )}
        </div>
      )}
    </Popover2>
  );
};
ActionDropdown.propTypes = {
  type: PropTypes.string.isRequired,
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
    _isArchived: PropTypes.bool,
    _raw: PropTypes.object,
  }).isRequired,
  actions: PropTypes.shape({
    onView: PropTypes.func,
    onEdit: PropTypes.func,
    onStartRename: PropTypes.func,
    onStatusToggle: PropTypes.func,
    onShare: PropTypes.func,
    onDuplicate: PropTypes.func,
    onSaveAsShell: PropTypes.func,
    onCreateFromShell: PropTypes.func,
    onDelete: PropTypes.func,
  }).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * Count cell that shows a small spinner when the count is empty and the
 * cadence/message is Active — indicating enrollment is likely in progress.
 * Uses a plain CSS spinner instead of <Loading> which renders an absolute-positioned
 * overlay that covers the entire table.
 */
export const EnrollingCountCell = ({ value, status }) => {
  if (value != null && value > 0) return value.toLocaleString();
  // Only show "Enrolling…" when stats haven't loaded yet (null) and cadence is active.
  // When value is 0 the stats have loaded — the cadence genuinely has no members.
  if ((status || '').toLowerCase() === 'active' && value == null) {
    return (
      <span className="cadences-v2-table--enrolling-count">
        <span className="cadences-v2-table--enrolling-spinner" />
        <span>Enrolling…</span>
      </span>
    );
  }
  return value != null ? value.toLocaleString() : '\u2014';
};
EnrollingCountCell.propTypes = {
  value: PropTypes.number,
  status: PropTypes.string,
};

/**
 * Renders the title cell with icon, name link, sharing badge, and optional step subtitle.
 * Supports inline rename mode.
 */
export const TitleCell = ({ row, icon, iconKit, onView, renamingState }) => {
  const { renamingId, renameValue, onRenameValueChange, onSubmitRename, onCancelRename } = renamingState || {};

  if (String(row.id) === String(renamingId)) {
    return (
      <div className="cadences-v2-table--name">
        <Icon icon={icon} iconKit={iconKit} className="cadences-v2-table--name-icon" />
        <input
          type="text"
          className="cadences-v2-table--rename-input"
          value={renameValue}
          onChange={(e) => onRenameValueChange(e.target.value)}
          onBlur={() => onSubmitRename(row.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmitRename(row.id);
            else if (e.key === 'Escape') onCancelRename();
            else e.stopPropagation();
          }}
          autoFocus
        />
      </div>
    );
  }

  // canView is only ever explicitly false for rows merged in to fill out a
  // blueprint-filtered list (see cadences-v2-controller.js) — every other
  // list query already drops anything the viewer can't open before it gets here.
  const canOpen = row._raw?.canView !== false;

  return (
    <div className="cadences-v2-table--name">
      <Icon icon={icon} iconKit={iconKit} className="cadences-v2-table--name-icon" />
      <div className="cadences-v2-table--name-content">
        <div className="cadences-v2-table--name-row">
          {canOpen ? (
            <button type="button" className="cadences-v2-table--name-link" onClick={() => onView(row.id)}>
              {row.title || row.name}
            </button>
          ) : (
            <span
              className="cadences-v2-table--name-restricted"
              title="You don't have permission to open this — it's shown so counts stay accurate."
            >
              <Icon icon="lock" size={1} />
              {row.title || row.name}
            </span>
          )}
          {row._raw?.sharing?.length > 0 && (
            <span className="cadences-v2-table--shared-badge" title={`Shared with ${row._raw.sharing.length} people`}>
              <Icon icon="users" />
            </span>
          )}
        </div>
        {row.subtitle && <span className="cadences-v2-table--name-subtitle">{row.subtitle}</span>}
      </div>
    </div>
  );
};
TitleCell.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    name: PropTypes.string,
    subtitle: PropTypes.string,
    _raw: PropTypes.shape({
      sharing: PropTypes.arrayOf(PropTypes.any),
      canView: PropTypes.bool,
    }),
  }).isRequired,
  icon: PropTypes.string.isRequired,
  iconKit: PropTypes.string,
  onView: PropTypes.func.isRequired,
  renamingState: PropTypes.shape({
    renamingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    renameValue: PropTypes.string,
    onRenameValueChange: PropTypes.func,
    onSubmitRename: PropTypes.func,
    onCancelRename: PropTypes.func,
  }),
};
